import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Copy, Check, ChevronDown, ChevronUp, MessageSquare, History, X, RefreshCw, Info } from 'lucide-react';
import { qaApi, QAResponse, QAHistoryEntry, QAHistoryData } from '@/services/api';
import { PageFrame, PageHeader, Surface, Button, Note, Pill, Placeholder } from '@/components/ui';
import styles from './QAPage.module.css';
import shared from '@/components/utils/shared.module.css';

interface QueryEntry {
    id: number;
    question: string;
    result: QAResponse | null;
    error: string | null;
    loading: boolean;
}

const SUGGESTIONS = [
    'What is the total AUM across all clients?',
    'Which clients hold the most shares of HDFC Bank?',
    'What was the total brokerage paid last month?',
    'Show me the top 10 holdings by current value',
];

// A result column is numeric when every non-null value is a number or a plain
// numeric string (Decimals often arrive as strings). Numeric columns are
// right-aligned in both the header and the cells.
const NUMERIC_STRING = /^[-+]?\d+(\.\d+)?$/;
function numericColumns(columnCount: number, rows: (string | number | null)[][]): boolean[] {
    return Array.from({ length: columnCount }, (_, ci) => {
        let seen = false;
        for (const row of rows) {
            const v = row[ci];
            if (v === null || v === undefined || v === '') continue;
            if (typeof v === 'number' || (typeof v === 'string' && NUMERIC_STRING.test(v.trim()))) {
                seen = true;
            } else {
                return false;
            }
        }
        return seen;
    });
}

const alignStyle = (numeric: boolean): React.CSSProperties | undefined =>
    numeric ? { textAlign: 'right' } : undefined;

function formatRelativeTime(isoString: string): string {
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

const QAPage: React.FC = () => {
    const [question, setQuestion] = useState('');
    const [chatHistory, setChatHistory] = useState<QueryEntry[]>([]);
    const nextIdRef = useRef(1);
    const [expandedSql, setExpandedSql] = useState<Set<number>>(new Set());
    const [expandedTable, setExpandedTable] = useState<Set<number>>(new Set());
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [copyFailedId, setCopyFailedId] = useState<number | null>(null);

    // History drawer state
    const [showHistory, setShowHistory] = useState(false);
    const [drawerMounted, setDrawerMounted] = useState(false);
    const [historyEntries, setHistoryEntries] = useState<QAHistoryEntry[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [historyModal, setHistoryModal] = useState<{
        entry: QAHistoryEntry;
        data: QAHistoryData | null;
        loading: boolean;
        error: string | null;
    } | null>(null);

    const messagesRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const isLoading = chatHistory.some(e => e.loading);

    useEffect(() => {
        const el = messagesRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [chatHistory]);

    // Mount drawer immediately on open; delay unmount on close so the slide-out transition completes
    useEffect(() => {
        if (showHistory) {
            setDrawerMounted(true);
        } else {
            const timer = setTimeout(() => setDrawerMounted(false), 250);
            return () => clearTimeout(timer);
        }
    }, [showHistory]);

    const handleTextareaInput = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    }, []);

    const fetchHistory = useCallback(async () => {
        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const data = await qaApi.getHistory(50);
            setHistoryEntries(data);
        } catch {
            setHistoryError('Failed to load history.');
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    const openHistory = useCallback(() => {
        setShowHistory(true);
        fetchHistory();
    }, [fetchHistory]);

    const submit = useCallback(async (q: string) => {
        const trimmed = q.trim();
        if (!trimmed || isLoading) return;

        const id = nextIdRef.current;
        nextIdRef.current += 1;
        setQuestion('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.focus();
        }

        setChatHistory(prev => [...prev, { id, question: trimmed, result: null, error: null, loading: true }]);

        try {
            const result = await qaApi.ask(trimmed);
            setChatHistory(prev => prev.map(e => e.id === id ? { ...e, result, loading: false } : e));
            if (result.rows.length > 0) {
                setExpandedTable(prev => new Set(prev).add(id));
            }
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Something went wrong. Please try again.';
            setChatHistory(prev => prev.map(e => e.id === id ? { ...e, error: msg, loading: false } : e));
        }
    }, [isLoading]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit(question);
        }
    }, [question, submit]);

    const toggleSql = useCallback((id: number) => {
        setExpandedSql(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const toggleTable = useCallback((id: number) => {
        setExpandedTable(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }, []);

    const copyTable = useCallback((entry: QueryEntry) => {
        if (!entry.result) return;
        const { columns, rows } = entry.result;
        const tsv = [
            columns.join('\t'),
            ...rows.map(row => row.map(cell => (cell === null ? '' : String(cell))).join('\t')),
        ].join('\n');
        navigator.clipboard.writeText(tsv).then(() => {
            setCopiedId(entry.id);
            setTimeout(() => setCopiedId(prev => (prev === entry.id ? null : prev)), 2000);
        }).catch(() => {
            setCopyFailedId(entry.id);
            setTimeout(() => setCopyFailedId(prev => (prev === entry.id ? null : prev)), 2000);
        });
    }, []);

    const rerunFromHistory = useCallback((q: string) => {
        setShowHistory(false);
        submit(q);
    }, [submit]);

    const openHistoryModal = useCallback(async (entry: QAHistoryEntry) => {
        if (entry.has_error) {
            setHistoryModal({ entry, data: null, loading: false, error: null });
            return;
        }
        setHistoryModal({ entry, data: null, loading: true, error: null });
        try {
            const data = await qaApi.getHistoryData(entry.id);
            setHistoryModal(prev => prev?.entry.id === entry.id ? { ...prev, data, loading: false } : prev);
        } catch {
            setHistoryModal(prev => prev?.entry.id === entry.id ? { ...prev, error: 'Failed to load data.', loading: false } : prev);
        }
    }, []);

    const cell = (val: string | number | null) => (val === null || val === undefined ? '—' : String(val));

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Portfolio Q&A"
                    subtitle="Ask anything about your portfolio in plain English."
                    actions={
                        <Button onClick={openHistory} title="View past questions" icon={<History size={14} />}>
                            History
                        </Button>
                    }
                />
            }
        >
            <Surface className={styles.card}>

                {/* Messages */}
                <div className={styles.messages} ref={messagesRef}>
                    {chatHistory.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>
                                <MessageSquare size={24} />
                            </div>
                            <p className={styles.emptyTitle}>Ask a question about your portfolio</p>
                            <p className={styles.emptySubtitle}>
                                Get instant answers powered by AI — from AUM summaries to trade history.
                            </p>
                            <div className={styles.suggestions}>
                                {SUGGESTIONS.map(s => (
                                    <Button key={s} size="sm" onClick={() => setQuestion(s)}>
                                        {s}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        chatHistory.map(entry => (
                            <div key={entry.id} className={styles.messageGroup}>
                                <div className={styles.userMessage}>{entry.question}</div>
                                <div className={styles.assistantMessage}>
                                    {entry.loading && (
                                        <div className={styles.typingIndicator}>
                                            <span className={styles.typingDot} />
                                            <span className={styles.typingDot} />
                                            <span className={styles.typingDot} />
                                        </div>
                                    )}
                                    {entry.error && (
                                        <Note tone="error">{entry.error}</Note>
                                    )}
                                    {entry.result && (
                                        <div className={styles.resultBody}>
                                            {entry.result.answer && (
                                                <p className={styles.answerText}>{entry.result.answer}</p>
                                            )}
                                            <div className={styles.metaRow}>
                                                <Pill solid plain>
                                                    {entry.result.latency_ms.toLocaleString('en-IN')} ms
                                                </Pill>
                                                <Pill solid plain>
                                                    {(entry.result.prompt_tokens + entry.result.output_tokens).toLocaleString('en-IN')} tokens
                                                </Pill>
                                                {entry.result.rows.length > 0 && (
                                                    <Pill tone="info" solid plain>
                                                        {entry.result.rows.length} row{entry.result.rows.length !== 1 ? 's' : ''}
                                                    </Pill>
                                                )}
                                            </div>
                                            {entry.result.rows.length > 0 && (
                                                <div className={styles.section}>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => toggleTable(entry.id)}
                                                        icon={expandedTable.has(entry.id) ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                    >
                                                        Data
                                                        <span className={styles.sectionMeta}>
                                                            &nbsp;({entry.result.rows.length} × {entry.result.columns.length})
                                                        </span>
                                                    </Button>
                                                    {expandedTable.has(entry.id) && (
                                                        <>
                                                            <div className={styles.tableToolbar}>
                                                                <Button
                                                                    size="sm"
                                                                    variant={copiedId === entry.id ? 'success' : copyFailedId === entry.id ? 'danger' : 'secondary'}
                                                                    onClick={() => copyTable(entry)}
                                                                    title="Copy as TSV"
                                                                    icon={copiedId === entry.id ? <Check size={12} /> : <Copy size={12} />}
                                                                >
                                                                    {copiedId === entry.id ? 'Copied' : copyFailedId === entry.id ? 'Failed' : 'Copy'}
                                                                </Button>
                                                            </div>
                                                            <div className={styles.tableWrapper}>
                                                                <div className={styles.tableScroll}>
                                                                    {(() => {
                                                                    const numeric = numericColumns(entry.result.columns.length, entry.result.rows);
                                                                    return (
                                                                    <table className={`${shared.table} ${styles.dataTable}`}>
                                                                        <thead>
                                                                            <tr>
                                                                                {entry.result.columns.map((col, ci) => <th key={col} style={alignStyle(numeric[ci])}>{col}</th>)}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {entry.result.rows.map((row, ri) => (
                                                                                <tr key={ri}>
                                                                                    {row.map((v, ci) => <td key={ci} style={alignStyle(numeric[ci])}>{cell(v)}</td>)}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                    );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            <div className={styles.section}>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => toggleSql(entry.id)}
                                                    icon={expandedSql.has(entry.id) ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                >
                                                    Generated SQL
                                                </Button>
                                                {expandedSql.has(entry.id) && (
                                                    <div className={styles.sqlBlock}>
                                                        <pre className={styles.sqlCode}>{entry.result.sql}</pre>
                                                        {entry.result.params.length > 0 && (
                                                            <div className={styles.sqlParams}>
                                                                <span className={styles.sqlParamsLabel}>Params:</span>
                                                                {entry.result.params.map((p, i) => (
                                                                    <code key={i} className={styles.sqlParamChip}>{p === null ? 'NULL' : String(p)}</code>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Input area */}
                <div className={styles.inputArea}>
                    <div className={styles.inputWrapper}>
                        <textarea
                            ref={textareaRef}
                            className={styles.textarea}
                            value={question}
                            rows={1}
                            placeholder="Ask anything about your portfolio…"
                            disabled={isLoading}
                            onChange={e => { setQuestion(e.target.value); handleTextareaInput(); }}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            variant="primary"
                            className={styles.sendBtn}
                            disabled={!question.trim() || isLoading}
                            onClick={() => submit(question)}
                            title="Send (Enter)"
                            aria-label="Send"
                        >
                            <Send size={16} />
                        </Button>
                    </div>
                    <div className={styles.contextWarning}>
                        <Info size={12} />
                        Each question is answered independently — the AI has no memory of your previous questions.
                    </div>
                    <p className={styles.inputHint}>Enter to send · Shift+Enter for new line</p>
                </div>
            </Surface>

            {/* History drawer backdrop */}
            {showHistory && (
                <div className={styles.historyBackdrop} onClick={() => setShowHistory(false)} />
            )}

            {/* History detail modal */}
            {historyModal && (
                <div className={styles.historyModalBackdrop} onClick={() => setHistoryModal(null)}>
                    <div className={styles.historyModal} onClick={e => e.stopPropagation()}>
                        <div className={styles.historyModalHeader}>
                            <p className={styles.historyModalQuestion}>{historyModal.entry.question}</p>
                            <Button iconOnly onClick={() => setHistoryModal(null)} title="Close" aria-label="Close">
                                <X size={16} />
                            </Button>
                        </div>
                        <div className={styles.historyModalBody}>
                            {historyModal.loading && (
                                <div className={styles.typingIndicator} style={{ margin: '12px 0' }}>
                                    <span className={styles.typingDot} />
                                    <span className={styles.typingDot} />
                                    <span className={styles.typingDot} />
                                </div>
                            )}
                            {historyModal.entry.has_error && (
                                <Note tone="error">This question failed to execute.</Note>
                            )}
                            {historyModal.error && (
                                <Note tone="error">{historyModal.error}</Note>
                            )}
                            {historyModal.data && (
                                <>
                                    {historyModal.entry.answer && (
                                        <p className={styles.historyModalAnswer}>{historyModal.entry.answer}</p>
                                    )}
                                    <div className={styles.metaRow}>
                                        <span className={styles.historyTime}>{formatRelativeTime(historyModal.entry.created_ts)}</span>
                                        {historyModal.data.rows.length > 0 && (
                                            <Pill tone="info" solid plain>
                                                {historyModal.data.rows.length} row{historyModal.data.rows.length !== 1 ? 's' : ''}
                                            </Pill>
                                        )}
                                    </div>
                                    {historyModal.data.rows.length > 0 && (
                                        <div className={styles.historyModalSection}>
                                            <p className={styles.historyModalSectionTitle}>Data</p>
                                            <div className={styles.tableWrapper}>
                                                <div className={styles.tableScroll}>
                                                    {(() => {
                                                    const numeric = numericColumns(historyModal.data.columns.length, historyModal.data.rows);
                                                    return (
                                                    <table className={`${shared.table} ${styles.dataTable}`}>
                                                        <thead>
                                                            <tr>{historyModal.data.columns.map((col, ci) => <th key={col} style={alignStyle(numeric[ci])}>{col}</th>)}</tr>
                                                        </thead>
                                                        <tbody>
                                                            {historyModal.data.rows.map((row, ri) => (
                                                                <tr key={ri}>{row.map((v, ci) => <td key={ci} style={alignStyle(numeric[ci])}>{cell(v)}</td>)}</tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className={styles.historyModalSection}>
                                        <p className={styles.historyModalSectionTitle}>Generated SQL</p>
                                        <div className={styles.sqlBlock}>
                                            <pre className={styles.sqlCode}>{historyModal.data.sql}</pre>
                                            {historyModal.data.params.length > 0 && (
                                                <div className={styles.sqlParams}>
                                                    <span className={styles.sqlParamsLabel}>Params:</span>
                                                    {historyModal.data.params.map((p, i) => (
                                                        <code key={i} className={styles.sqlParamChip}>{p === null ? 'NULL' : String(p)}</code>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className={styles.historyModalFooter}>
                                {!historyModal.entry.has_error && (
                                    <Button
                                        size="sm"
                                        disabled={isLoading}
                                        onClick={() => { setHistoryModal(null); rerunFromHistory(historyModal.entry.question); }}
                                    >
                                        Ask again
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* History drawer */}
            {drawerMounted && <div className={`${styles.historyDrawer} ${showHistory ? styles.historyDrawerOpen : ''}`}>
                <div className={styles.historyDrawerHeader}>
                    <div className={styles.historyDrawerTitle}>
                        <History size={15} color="var(--accent)" />
                        Past Questions
                    </div>
                    <div className={styles.historyDrawerActions}>
                        <Button iconOnly onClick={fetchHistory} title="Refresh" aria-label="Refresh" disabled={historyLoading}>
                            <RefreshCw size={13} className={historyLoading ? styles.spinning : ''} />
                        </Button>
                        <Button iconOnly onClick={() => setShowHistory(false)} title="Close" aria-label="Close">
                            <X size={15} />
                        </Button>
                    </div>
                </div>

                <div className={styles.historyDrawerBody}>
                    {historyLoading && historyEntries.length === 0 && (
                        <Placeholder kind="loading" message="Loading…" />
                    )}
                    {historyError && (
                        <div className={styles.historyFetchError}><Note tone="error">{historyError}</Note></div>
                    )}
                    {!historyLoading && !historyError && historyEntries.length === 0 && (
                        <Placeholder message="No history yet. Ask a question to get started." />
                    )}
                    {historyEntries.map(entry => (
                        <div
                            key={entry.id}
                            className={`${styles.historyItem} ${entry.has_error ? styles.historyItemError : ''}`}
                            onClick={() => openHistoryModal(entry)}
                        >
                            <p className={styles.historyQuestion}>{entry.question}</p>
                            {entry.answer && !entry.has_error && (
                                <p className={styles.historyAnswer}>{entry.answer}</p>
                            )}
                            {entry.has_error && (
                                <p className={styles.historyErrorLabel}>Failed</p>
                            )}
                            <div className={styles.historyMeta}>
                                <span className={styles.historyTime}>{formatRelativeTime(entry.created_ts)}</span>
                                {entry.row_count > 0 && (
                                    <span className={styles.historyRows}>{entry.row_count} rows</span>
                                )}
                                {!entry.has_error && (
                                    <Button
                                        size="sm"
                                        className={styles.historyRerunBtn}
                                        disabled={isLoading}
                                        onClick={e => { e.stopPropagation(); rerunFromHistory(entry.question); }}
                                        title="Ask this question again"
                                    >
                                        Ask again
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>}
    </PageFrame>
    );
};

export default QAPage;
